import {
  AccountProductEnum,
  AccountTypeEnum,
  ExternalDebitCardTransferOption,
  Tag,
  TransferDestinationInput,
  TransferDestinationTypeInput,
  TransferSourceInput,
  TransferSourceTypeInput,
} from '@varo/graphql-schema-types';
import { toTitleCase } from 'shared/utils/string/string.helpers';

type TransferSpeedTag = Pick<Tag, 'icon' | 'id' | 'tagColor' | 'text'>;

interface BaseTransferOption {
  id: string;
  primaryText: string;
  secondaryText?: string;
  isSelectable: boolean;
  unavailableReason?: string;
  iconUrl?: string;
  tag?: TransferSpeedTag;
}

interface TransferOptionAccount {
  id: string;
  type: AccountTypeEnum;
  product: AccountProductEnum;
  nickname: string;
  iconUrl?: string;
  accountNumberLast4?: string | null;
  availableBalance?: {
    formatted: string;
  } | null;
  transferInfo?: {
    selectable?: boolean | null;
    message?: string | null;
  } | null;
  institution?: {
    name: string;
  } | null;
  transferSpeedTag?: TransferSpeedTag | null;
}

interface TransferOptionAccountGroups {
  internal: TransferOptionAccount[];
  external: TransferOptionAccount[];
}

interface TransferOptionFallbackText {
  account: string;
  externalAccount: string;
  formatAvailableBalance: (balance: string) => string;
}

export interface TransferSourceOption extends BaseTransferOption {
  kind: 'account';
  accountProduct: AccountProductEnum;
  accountType: AccountTypeEnum;
  input: TransferSourceInput;
}

interface AccountTransferDestinationOption extends BaseTransferOption {
  kind: 'account';
  accountProduct: AccountProductEnum;
  accountType: AccountTypeEnum;
  input: TransferDestinationInput;
}

interface CardTransferDestinationOption extends BaseTransferOption {
  kind: 'externalDebitCard';
  input: TransferDestinationInput;
}

export type TransferDestinationOption =
  AccountTransferDestinationOption | CardTransferDestinationOption;

function getAccountPrimaryText(
  account: TransferOptionAccount,
  fallbackText: TransferOptionFallbackText,
) {
  const institutionName = account.institution?.name.trim();
  const nickname = account.nickname.trim();
  let accountName = nickname;
  if (account.type === AccountTypeEnum.External) {
    accountName = institutionName
      ? `${institutionName} ${toTitleCase(account.product)}`
      : nickname || fallbackText.externalAccount;
  } else if (!accountName) {
    accountName = [institutionName, fallbackText.account]
      .filter(Boolean)
      .join(' ');
  }
  return account.accountNumberLast4
    ? `${accountName} • ${account.accountNumberLast4}`
    : accountName;
}

function getAccountSecondaryText(
  account: TransferOptionAccount,
  fallbackText: TransferOptionFallbackText,
) {
  return account.availableBalance?.formatted
    ? fallbackText.formatAvailableBalance(account.availableBalance.formatted)
    : undefined;
}

function adaptAccountOption(
  account: TransferOptionAccount,
  fallbackText: TransferOptionFallbackText,
) {
  return {
    kind: 'account',
    id: account.id,
    accountProduct: account.product,
    accountType: account.type,
    primaryText: getAccountPrimaryText(account, fallbackText),
    secondaryText: getAccountSecondaryText(account, fallbackText),
    isSelectable: account.transferInfo?.selectable === true,
    unavailableReason: account.transferInfo?.message ?? undefined,
    iconUrl: account.iconUrl,
  } as const;
}

function adaptAccountDestinationOptions(
  accounts: TransferOptionAccount[],
  fallbackText: TransferOptionFallbackText,
) {
  return accounts.map<AccountTransferDestinationOption>((account) => ({
    ...adaptAccountOption(account, fallbackText),
    input: {
      id: account.id,
      type: TransferDestinationTypeInput.Account,
    },
    tag: account.transferSpeedTag ?? undefined,
  }));
}

export function adaptTransferSourceOptions(
  groupedAccounts: TransferOptionAccountGroups | undefined,
  fallbackText: TransferOptionFallbackText,
): TransferSourceOption[] {
  if (!groupedAccounts) {
    return [];
  }

  return [...groupedAccounts.internal, ...groupedAccounts.external].map(
    (account) => ({
      ...adaptAccountOption(account, fallbackText),
      input: {
        id: account.id,
        type: TransferSourceTypeInput.Account,
      },
    }),
  );
}

export function adaptTransferDestinationOptions({
  groupedAccounts,
  externalDebitCards,
  fallbackText,
}: {
  groupedAccounts: TransferOptionAccountGroups | undefined;
  externalDebitCards: (ExternalDebitCardTransferOption & {
    transferSpeedTag?: TransferSpeedTag | null;
  })[];
  fallbackText: TransferOptionFallbackText;
}): TransferDestinationOption[] {
  const internalAccountOptions = adaptAccountDestinationOptions(
    groupedAccounts?.internal ?? [],
    fallbackText,
  );
  const externalAccountOptions = adaptAccountDestinationOptions(
    groupedAccounts?.external ?? [],
    fallbackText,
  );
  const cardOptions = externalDebitCards.map<CardTransferDestinationOption>(
    (card) => ({
      kind: 'externalDebitCard',
      id: card.id,
      input: {
        id: card.id,
        type: TransferDestinationTypeInput.ExternalDebitCard,
      },
      primaryText: `${card.displayName} • ${card.lastFour}`,
      isSelectable: card.isSelectable,
      tag: card.transferSpeedTag ?? undefined,
    }),
  );

  return [...internalAccountOptions, ...cardOptions, ...externalAccountOptions];
}
